import {
  Box,
  Center,
  Flex,
  Text,
  useToast,
  Spinner,
  Badge,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { useEffect, useState, useRef } from "react";
import {
  previewEligible,
  PreviewEligibleRequest,
  PreviewEligibleResponse,
  Project,
  VotingPeriod,
  addEligibleProject,
  removeEligibleProject,
  getVotingPeriodProjects,
  VotingPeriodProject,
  Criterion,
} from "utils/apiService/PinkSale";

// Format date
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

// Get chain name
const getChainName = (chainId: number) => {
  const chainMap: { [key: number]: string } = {
    1: "Ethereum",
    56: "BSC",
    501424: "Solana",
  };
  return chainMap[chainId] || `Chain ${chainId}`;
};

// Format currency
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

interface PreviewEligibleProjectTabProps {
  votingPeriod: VotingPeriod | null;
}

const PreviewEligibleProjectTab = ({
  votingPeriod,
}: PreviewEligibleProjectTabProps) => {
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const {
    isOpen: isJsonModalOpen,
    onOpen: onJsonModalOpen,
    onClose: onJsonModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddDialogOpen,
    onOpen: onAddDialogOpen,
    onClose: onAddDialogClose,
  } = useDisclosure();
  const {
    isOpen: isRemoveDialogOpen,
    onOpen: onRemoveDialogOpen,
    onClose: onRemoveDialogClose,
  } = useDisclosure();
  const {
    isOpen: isEditCriteriaDialogOpen,
    onOpen: onEditCriteriaDialogOpen,
    onClose: onEditCriteriaDialogClose,
  } = useDisclosure();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] =
    useState<PreviewEligibleResponse | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedProjectForAction, setSelectedProjectForAction] =
    useState<Project | null>(null);
  const [addingProject, setAddingProject] = useState<string | null>(null);
  const [removingProject, setRemovingProject] = useState<string | null>(null);
  const [votingPeriodProjects, setVotingPeriodProjects] = useState<
    VotingPeriodProject[]
  >([]);
  const [criteriaJson, setCriteriaJson] = useState<string>("");
  const [criteriaError, setCriteriaError] = useState<string>("");
  const [originalCriteria, setOriginalCriteria] = useState<Criterion[]>([]);
  const [isSubmittingCriteria, setIsSubmittingCriteria] = useState(false);

  // Fetch preview eligible projects when voting period is loaded
  useEffect(() => {
    const fetchPreviewEligible = async () => {
      if (!votingPeriod) {
        return;
      }

      try {
        setPreviewLoading(true);
        const now = new Date();
        const previewRequest: PreviewEligibleRequest = {
          weekNumber: votingPeriod.weekNumber,
          year: votingPeriod.year,
          timestamp: now.toISOString(),
          criteria: votingPeriod.eligibilityCriteria || [],
          isPreviewAllProjects: true,
        };
        const data = await previewEligible(previewRequest);
        setPreviewData(data);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message || "Failed to preview eligible projects",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreviewEligible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [votingPeriod?._id]);

  // Fetch voting period projects to check if projects are already added
  useEffect(() => {
    const fetchVotingPeriodProjects = async () => {
      if (!votingPeriod) {
        return;
      }

      try {
        const projects = await getVotingPeriodProjects(votingPeriod._id);
        setVotingPeriodProjects(projects);
      } catch (error: any) {
        // Silently fail - we'll just not show remove button if we can't fetch
        console.error("Failed to fetch voting period projects:", error);
      }
    };

    fetchVotingPeriodProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [votingPeriod?._id]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleRefresh = async () => {
    if (!votingPeriod) return;

    try {
      setPreviewLoading(true);
      const now = new Date();
      const previewRequest: PreviewEligibleRequest = {
        weekNumber: votingPeriod.weekNumber,
        year: votingPeriod.year,
        timestamp: now.toISOString(),
        criteria: votingPeriod.eligibilityCriteria || [],
        isPreviewAllProjects: true,
      };
      const data = await previewEligible(previewRequest);
      setPreviewData(data);
      toast({
        title: "Success",
        description: "Preview refreshed",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to refresh preview",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleViewRawJson = (project: Project) => {
    setSelectedProject(project);
    onJsonModalOpen();
  };

  const handleAddEligibleProjectClick = (project: Project) => {
    setSelectedProjectForAction(project);
    onAddDialogOpen();
  };

  const handleAddEligibleProject = async () => {
    if (!votingPeriod || !selectedProjectForAction) return;

    try {
      setAddingProject(selectedProjectForAction.launchpadId);
      await addEligibleProject(votingPeriod._id, {
        launchpadId: selectedProjectForAction.launchpadId,
      });

      // Refresh voting period projects list
      const projects = await getVotingPeriodProjects(votingPeriod._id);
      setVotingPeriodProjects(projects);

      toast({
        title: "Success",
        description: "Project added to eligible projects successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onAddDialogClose();
      setSelectedProjectForAction(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to add eligible project",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAddingProject(null);
    }
  };

  const handleRemoveEligibleProjectClick = (project: Project) => {
    setSelectedProjectForAction(project);
    onRemoveDialogOpen();
  };

  const handleRemoveEligibleProject = async () => {
    if (!votingPeriod || !selectedProjectForAction) return;

    try {
      setRemovingProject(selectedProjectForAction.launchpadId);

      // Find the project in voting period projects by launchpadId
      const votingProject = votingPeriodProjects.find(
        (p) => p.launchpadId === selectedProjectForAction.launchpadId
      );

      if (!votingProject) {
        throw new Error("Project not found in eligible projects list");
      }

      await removeEligibleProject(
        votingProject.campaignId,
        votingProject.launchpadId
      );

      // Refresh voting period projects list
      const projects = await getVotingPeriodProjects(votingProject.campaignId);
      setVotingPeriodProjects(projects);

      toast({
        title: "Success",
        description: "Project removed from eligible projects successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onRemoveDialogClose();
      setSelectedProjectForAction(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to remove eligible project",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRemovingProject(null);
    }
  };

  // Check if project is eligible (all criteria passed)
  const isProjectEligible = (project: Project): boolean => {
    return Object.values(project.evaluationDetails).every(
      (detail) => detail.passed
    );
  };

  // Check if project is already in voting period projects
  const isProjectAdded = (project: Project): boolean => {
    return votingPeriodProjects.some(
      (p) => p.launchpadId === project.launchpadId
    );
  };

  // Handle open edit criteria dialog
  const handleOpenEditCriteriaDialog = () => {
    if (!votingPeriod) return;
    const criteria = votingPeriod.eligibilityCriteria || [];
    setOriginalCriteria(JSON.parse(JSON.stringify(criteria))); // Deep copy
    setCriteriaJson(JSON.stringify(criteria, null, 2));
    setCriteriaError("");
    onEditCriteriaDialogOpen();
  };

  // Handle criteria JSON change
  const handleCriteriaJsonChange = (value: string) => {
    setCriteriaJson(value);
    setCriteriaError("");

    if (!value.trim()) {
      setCriteriaError("Criteria cannot be empty");
      return;
    }

    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) {
        setCriteriaError("Criteria must be an array");
        return;
      }
    } catch (error: any) {
      setCriteriaError(
        error?.message || "Invalid JSON format. Please check your syntax."
      );
    }
  };

  // Handle reset criteria
  const handleResetCriteria = () => {
    setCriteriaJson(JSON.stringify(originalCriteria, null, 2));
    setCriteriaError("");
  };

  // Handle submit criteria (only for preview, not saving to voting period)
  const handleSubmitCriteria = async () => {
    if (!votingPeriod) return;

    if (criteriaError) {
      toast({
        title: "Validation Error",
        description: "Please fix the criteria JSON before submitting",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmittingCriteria(true);
      const parsedCriteria = JSON.parse(criteriaJson);
      if (!Array.isArray(parsedCriteria)) {
        toast({
          title: "Validation Error",
          description: "Criteria must be an array",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Only refresh preview data with new criteria (not saving to voting period)
      const now = new Date();
      const previewRequest: PreviewEligibleRequest = {
        weekNumber: votingPeriod.weekNumber,
        year: votingPeriod.year,
        timestamp: now.toISOString(),
        criteria: parsedCriteria,
        isPreviewAllProjects: true,
      };
      const data = await previewEligible(previewRequest);
      setPreviewData(data);

      toast({
        title: "Success",
        description: "Preview refreshed with updated criteria",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onEditCriteriaDialogClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to refresh preview",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingCriteria(false);
    }
  };

  return (
    <>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="lg" fontWeight="semibold">
          Preview Eligible Projects
          {previewData && ` (${previewData.totalEligible})`}
        </Text>
        <HStack spacing={2}>
          <Button
            size="sm"
            colorScheme="purple"
            onClick={handleOpenEditCriteriaDialog}
            isDisabled={!votingPeriod}
          >
            Edit Preview Filter
          </Button>
          <IconButton
            aria-label="Reload preview"
            icon={<RepeatIcon />}
            onClick={handleRefresh}
            isLoading={previewLoading}
            size="sm"
            colorScheme="blue"
            variant="ghost"
          />
        </HStack>
      </Flex>
      {previewLoading ? (
        <Center py={10}>
          <Spinner size="xl" />
        </Center>
      ) : previewData && previewData.projects ? (
        <>
          {previewData.eligibilityPeriod && (
            <Box p={4} bg="gray.50" borderRadius="md" mb={4}>
              <Text fontWeight="semibold" mb={2}>
                Eligibility Period:
              </Text>
              <Text fontSize="sm">
                Start: {formatDate(previewData.eligibilityPeriod.startTime)}
              </Text>
              <Text fontSize="sm">
                End: {formatDate(previewData.eligibilityPeriod.endTime)}
              </Text>
            </Box>
          )}
          {previewData.projects.length > 0 ? (
            <Box overflowX="auto">
              <Table
                variant="simple"
                size="sm"
                bg="white"
                borderRadius="md"
                boxShadow="sm"
              >
                <Thead>
                  <Tr>
                    <Th>Token Name</Th>
                    <Th>Symbol</Th>
                    <Th>Chain</Th>
                    <Th>Pool Address</Th>
                    <Th>Pool ID</Th>
                    <Th>Total Raised (USD)</Th>
                    <Th>Finish Time</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {previewData.projects.map((project: Project) => (
                    <Tr key={project.launchpadId}>
                      <Td fontWeight="medium">{project.tokenName}</Td>
                      <Td>
                        <Badge colorScheme="blue">{project.tokenSymbol}</Badge>
                      </Td>
                      <Td>{getChainName(project.chainId)}</Td>
                      <Td>
                        <Text
                          fontSize="xs"
                          fontFamily="mono"
                          cursor="copy"
                          _hover={{ color: "blue.500" }}
                          onClick={() => handleCopy(project.poolAddress)}
                        >
                          {project.poolAddress.slice(0, 10)}...
                        </Text>
                      </Td>
                      <Td>
                        <Text
                          fontSize="xs"
                          fontFamily="mono"
                          cursor="copy"
                          _hover={{ color: "blue.500" }}
                          onClick={() => handleCopy(project.launchpadId)}
                        >
                          {project.launchpadId.slice(0, 10)}...
                        </Text>
                      </Td>
                      <Td>{formatCurrency(project.totalRaisedUSD)}</Td>
                      <Td fontSize="xs">{formatDate(project.finishTime)}</Td>
                      <Td>
                        {isProjectEligible(project) ? (
                          <Badge colorScheme="green">Eligible</Badge>
                        ) : (
                          <Badge colorScheme="red">Not Eligible</Badge>
                        )}
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          {isProjectEligible(project) &&
                          isProjectAdded(project) ? (
                            <Button
                              size="xs"
                              colorScheme="red"
                              onClick={() =>
                                handleRemoveEligibleProjectClick(project)
                              }
                              isLoading={
                                removingProject === project.launchpadId
                              }
                              loadingText="Removing..."
                            >
                              Remove
                            </Button>
                          ) : (
                            <Button
                              size="xs"
                              colorScheme="purple"
                              onClick={() =>
                                handleAddEligibleProjectClick(project)
                              }
                              isLoading={addingProject === project.launchpadId}
                              loadingText="Adding..."
                            >
                              Add Eligible
                            </Button>
                          )}
                          <Button
                            size="xs"
                            colorScheme="green"
                            onClick={() => handleViewRawJson(project)}
                          >
                            Raw JSON
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Center py={10}>
              <Text>No eligible projects found</Text>
            </Center>
          )}
        </>
      ) : (
        <Center py={10}>
          <Text>No preview data available</Text>
        </Center>
      )}

      {/* Raw JSON Modal */}
      <Modal isOpen={isJsonModalOpen} onClose={onJsonModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Project JSON Data - {selectedProject?.tokenName || ""} (
            {selectedProject?.tokenSymbol || ""})
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              maxH="70vh"
              overflowY="auto"
            >
              {selectedProject ? (
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    fontFamily: "monospace",
                    fontSize: "12px",
                  }}
                >
                  {JSON.stringify(selectedProject, null, 2)}
                </pre>
              ) : (
                <Text>No data selected</Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                if (selectedProject) {
                  navigator.clipboard.writeText(
                    JSON.stringify(selectedProject, null, 2)
                  );
                  toast({
                    title: "Copied",
                    description: "Raw data copied to clipboard",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                  });
                }
              }}
            >
              Copy to Clipboard
            </Button>
            <Button colorScheme="gray" onClick={onJsonModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Eligible Project Confirmation Dialog */}
      <AlertDialog
        isOpen={isAddDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onAddDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Add Eligible Project
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to add{" "}
              <strong>
                {selectedProjectForAction?.tokenName || ""} (
                {selectedProjectForAction?.tokenSymbol || ""})
              </strong>{" "}
              to eligible projects?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onAddDialogClose}
                isDisabled={!!addingProject}
              >
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleAddEligibleProject}
                ml={3}
                isLoading={!!addingProject}
              >
                Add
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Remove Eligible Project Confirmation Dialog */}
      <AlertDialog
        isOpen={isRemoveDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRemoveDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.500">
              Remove Eligible Project
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to remove{" "}
              <strong>
                {selectedProjectForAction?.tokenName || ""} (
                {selectedProjectForAction?.tokenSymbol || ""})
              </strong>{" "}
              from eligible projects? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onRemoveDialogClose}
                isDisabled={!!removingProject}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRemoveEligibleProject}
                ml={3}
                isLoading={!!removingProject}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Edit Criteria Dialog */}
      <Modal
        isOpen={isEditCriteriaDialogOpen}
        onClose={onEditCriteriaDialogClose}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Preview Filter</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl isInvalid={!!criteriaError}>
              <FormLabel>Criteria (JSON Array)</FormLabel>
              <Textarea
                value={criteriaJson}
                onChange={(e) => handleCriteriaJsonChange(e.target.value)}
                placeholder='[{"type": "minRaised", "enabled": true, "params": {"amountUsd": 1000}}]'
                fontFamily="monospace"
                fontSize="sm"
                minH="300px"
                resize="vertical"
              />
              {criteriaError && (
                <FormErrorMessage>{criteriaError}</FormErrorMessage>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                Enter criteria as a JSON array. Example:{" "}
                {`[{"type": "minRaised", "enabled": true, "params": {"amountUsd": 1000}}]`}
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="gray"
              mr={3}
              onClick={handleResetCriteria}
              isDisabled={isSubmittingCriteria}
            >
              Reset
            </Button>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleSubmitCriteria}
              isLoading={isSubmittingCriteria}
              loadingText="Submitting..."
            >
              Submit
            </Button>
            <Button
              onClick={onEditCriteriaDialogClose}
              isDisabled={isSubmittingCriteria}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PreviewEligibleProjectTab;
